import { fetchEntityIfNeeded } from '@classpass/saga-tools';
import { defineMessages } from 'react-intl';
import { initialize, SubmissionError } from 'redux-form';
import { createFormAction } from 'redux-form-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import * as logger from 'universal-logger';
import { deepExtract } from 'helpers/deep';
import * as ObjectUtil from 'helpers/object';
import { ga } from 'helpers/multitrack';
import { getSelectorHtml } from 'helpers/getSelectorHtml';
import { segment } from 'helpers/segment';
import { set as setUserType } from 'helpers/userTypeCookie';
import { selectUserLocale } from 'redux/modules/locale';
import { userVerifyEmail } from 'redux/saga/entities/bound';
import { getUserLocation } from 'redux/saga/helpers';
import { loadAccountCreationConsent } from 'components/Consent/Consent.saga';
import { GENERIC_ERROR } from 'components/Errors/ApiError';
import { CODE_BY_ERROR, createLead } from 'features/LeadGenForm/LeadGenForm.saga';

const messages = defineMessages({
  alreadyMember: {
    id: 'signup.lead.already-member',
    defaultMessage:
      'Looks like you already started creating a ClassPass account with this email address in the past. Please <a href="{loginLink}">log in</a> to complete your purchase.',
    hasHtml: true,
  },
  invalidEmail: {
    id: 'signup.lead.invalid-email',
    defaultMessage: 'Please check your email address for misspellings.',
  },
});

export const LEAD_FORM = 'signup-card-lead';
export const leadFormAction = createFormAction(LEAD_FORM);

export const initializeLeadForm = hiddenFields => ({
  type: `INITIALIZE_${LEAD_FORM}`,
  hiddenFields,
});

export function* doInitializeLeadForm(client, { hiddenFields }) {
  const consent = yield call(loadAccountCreationConsent, client);

  yield put(
    initialize(LEAD_FORM, {
      ...hiddenFields,
      ...ObjectUtil.pick(
        [
          'marketingOptInDisclaimer',
          'marketingOptInDefault',
          'marketingOptOutDisclaimer',
          'marketingOptOutDefault',
          'searchableDefault',
          'searchableDisclaimer',
        ],
        consent
      ),
      searchable: consent.searchableDefault,
      marketingOptIn: consent.marketingOptInDefault,
      marketingOptOut: consent.marketingOptOutDefault,
    })
  );
}

export function* doSubmitLeadForm(client, { payload: form }) {
  // TODO: Always check email against users and move errors accordingly
  const marketingDisclaimer = yield call(getSelectorHtml, '.marketing-disclaimer-toggle > .media__body');
  const searchableDisclaimer = yield call(getSelectorHtml, '.searchable-disclaimer-toggle > .media__body');
  const marketingValues = { marketingDisclaimer };

  if (form.marketingOptOutDisclaimer) {
    marketingValues.marketing = !form.marketingOptOut;
    marketingValues.marketingDefault = form.marketingOptOutDefault;
  } else if (form.marketingOptInDisclaimer) {
    marketingValues.marketing = form.marketingOptIn;
    marketingValues.marketingDefault = form.marketingOptInDefault;
  } else {
    marketingValues.marketing = true;
  }

  let emailError;
  let generalError;

  try {
    yield call(fetchEntityIfNeeded, userVerifyEmail, [client, form.email]);
  } catch (error) {
    if (error.httpStatus === 400) {
      yield call(setUserType, 'registered');
      const loginLink = `/login?returnTo=${form.resumeSignupLink}`;
      emailError = { ...messages.alreadyMember, values: { loginLink } };
      yield call(ga, 'Membership Purchase', 'Membership Purchase Lead Creation Error', 'Already member');
      yield call(segment.track, 'Membership Purchase', {
        action: 'Membership Purchase Lead Creation Error',
        label: 'Already member',
      });
    } else {
      yield call(logger.warn, 'Failed to verify email in LeadCard', {
        email: form.email,
        error,
      });
    }
  }

  if (!emailError && marketingValues.marketing) {
    try {
      const locale = yield select(selectUserLocale);
      const activeLocation = yield call(getUserLocation, client);
      yield call(createLead, client, form.email, { locale, location: activeLocation });
      yield call(segment.track, 'Membership Purchase', {
        action: 'Membership Purchase Lead Creation',
      });
      yield call(ga, 'Membership Purchase', 'Membership Purchase Lead Creation');
    } catch (error) {
      const errorCode = deepExtract('code', error);

      if (CODE_BY_ERROR.ALREADY_ON_WAITLIST === errorCode) {
        yield call(setUserType, 'lead');
      } else if (CODE_BY_ERROR.INVALID_EMAIL === errorCode) {
        emailError = messages.invalidEmail;
        yield call(ga, 'Membership Purchase', 'Membership Purchase Lead Creation Error', 'Invalid email');
        yield call(segment.track, 'Membership Purchase', {
          action: 'Membership Purchase Lead Creation Error',
          label: 'Invalid email',
        });
      } else {
        generalError = GENERIC_ERROR;
        yield call(ga, 'Membership Purchase', 'Membership Purchase Lead Creation Error', 'Unexpected error');
        yield call(segment.track, 'Membership Purchase', {
          action: 'Membership Purchase Lead Creation Error',
          label: 'Unexpected error',
        });
        yield call(logger.error, 'Failed to create lead during signup', error);
      }
    }
  }

  if (emailError || generalError) {
    return yield put(
      leadFormAction.failure(
        new SubmissionError({
          _error: generalError,
          email: emailError,
        })
      )
    );
  }

  return yield put(
    leadFormAction.success({
      email: form.email,
      ...marketingValues,
      ...(form.searchableDisclaimer
        ? {
            searchable: form.searchable,
            searchableDefault: form.searchableDefault,
            searchableDisclaimer,
          }
        : {}),
    })
  );
}

export function* watchLeadFormSagas(client) {
  yield takeEvery(initializeLeadForm().type, doInitializeLeadForm, client);
  yield takeEvery(leadFormAction.REQUEST, doSubmitLeadForm, client);
}
